import { useReducer, useEffect } from 'react';

import { useClientReviewSearchService } from './useClientReviewSearchService.js';
import { createClientReviewSearchService } from '../services/clientReviewSearchService.js';

import { PAGE_STATUSES } from './useWorkPageDetailService.js';
import { NO_WORKABLE_ENCOUNTERS, ALL_ENCOUNTERS_LOCKED } from '../constants.js';

const ACTION_TYPES = {
  INITIALIZE_PAGE_REQUEST: 'INITIALIZE_PAGE_REQUEST',
  INITIALZIZE_PAGE_SUCCESS: 'INITIALIZE_PAGE_SUCCESS',
  INITIALIZE_PAGE_FAILURE: 'INITIALIZE_PAGE_FAILURE',
  BEGIN_WORK_REQUEST: 'BEGIN_WORK_REQUEST',
  BEGIN_WORK_SUCCESS: 'BEGIN_WORK_SUCCESS',
  BEGIN_WORK_FAILURE: 'BEGIN_WORK_FAILURE',
  ACCEPT_ALL_REVIEWS_REQUEST: 'ACCEPT_ALL_REVIEWS_REQUEST',
  ACCEPT_ALL_REVIEWS_SUCCESS: 'ACCEPT_ALL_REVIEWS_SUCCESS',
  ACCEPT_ALL_REVIEWS_FAILURE: 'ACCEPT_ALL_REVIEWS_FAILURE'
};

const ACTIONS = {
  initializePageRequest: () => ({ type: ACTION_TYPES.INITIALIZE_PAGE_REQUEST }),
  initializePageSuccess: result => ({
    type: ACTION_TYPES.INITIALZIZE_PAGE_SUCCESS,
    payload: { ...result }
  }),
  initializePageFailure: error => ({
    type: ACTION_TYPES.INITIALIZE_PAGE_FAILURE,
    error
  }),
  beginWorkRequest: () => ({ type: ACTION_TYPES.BEGIN_WORK_REQUEST }),
  beginWorkSuccess: () => ({ type: ACTION_TYPES.BEGIN_WORK_SUCCESS }),
  beginWorkFailure: () => ({ type: ACTION_TYPES.BEGIN_WORK_FAILURE }),
  acceptAllReviewsRequest: () => ({ type: ACTION_TYPES.ACCEPT_ALL_REVIEWS_REQUEST }),
  acceptAllReviewsSuccess: () => ({ type: ACTION_TYPES.ACCEPT_ALL_REVIEWS_SUCCESS }),
  acceptAllReviewsFailure: error => ({ type: ACTION_TYPES.ACCEPT_ALL_REVIEWS_FAILURE, error })
};

const initialState = {
  status: PAGE_STATUSES.UNINITIALIZED,
  statusMessage: '',
  isRequestInProgress: false,
  errorMessage: null,
  kpiSection: {
    incompleteEncounterReviewsCount: null,
    completedEncounterReviewsCount: null,
    averageTimeUntilEncounterReviewCompleted: null,
    maxTimeEncounterReviewIncomplete: null,
    maxTimeLineItemUnworked: null,
    lineItemsRejectedCount: null,
    estimatedImpactOfRejectedLineItems: null
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.ACCEPT_ALL_REVIEWS_REQUEST: {
      return {
        ...state,
        isRequestInProgress: true,
        statusMessage: 'Accepting all encounter reviews, this may take a while'
      };
    }

    case ACTION_TYPES.ACCEPT_ALL_REVIEWS_SUCCESS: {
      return {
        ...state,
        isRequestInProgress: false,
        statusMessage: ''
      };
    }

    case ACTION_TYPES.ACCEPT_ALL_REVIEWS_FAILURE: {
      return {
        ...state,
        isRequestInProgress: false,
        statusMessage: '',
        status: PAGE_STATUSES.ERROR,
        errorMessage: 'Accept all reviews failed. Please refresh.'
      };
    }

    case ACTION_TYPES.INITIALIZE_PAGE_REQUEST: {
      return {
        ...state,
        isRequestInProgress: true
      };
    }

    case ACTION_TYPES.INITIALZIZE_PAGE_SUCCESS: {
      const { payload } = action;
      return {
        ...state,
        ...payload,
        isRequestInProgress: false,
        status: PAGE_STATUSES.INITIALIZED,
      };
    }

    case ACTION_TYPES.INITIALIZE_PAGE_FAILURE: {
      const { error } = action;
      return {
        ...state,
        isRequestInProgress: false,
        status: PAGE_STATUSES.ERROR,
        errorMessage: error
      };
    }

    case ACTION_TYPES.BEGIN_WORK_REQUEST: {
      return {
        ...state,
        isRequestInProgress: true,
        statusMessage: 'Searching for encounter'
      };
    }

    case ACTION_TYPES.BEGIN_WORK_SUCCESS: {
      return {
        ...state,
        isRequestInProgress: false,
        statusMessage: ''
      };
    }

    case ACTION_TYPES.BEGIN_WORK_FAILURE: {
      return {
        ...state,
        isRequestInProgress: false,
        status: PAGE_STATUSES.ERROR,
        statusMessage: ''
      };
    }

    default: {
      throw Error('useClientReviewLandingPage default case');
    }
  }
};

function useClientReviewLandingPage(service) {
  const [pageState, pageDispatch] = useReducer(reducer, initialState);
  const [searchState, searchApi] = useClientReviewSearchService(
    createClientReviewSearchService(service.serviceRoutes)
  );

  const initializePage = async () => {
    pageDispatch(ACTIONS.initializePageRequest());

    try {
      const jsonResponse = await service.initializePage();
      pageDispatch(ACTIONS.initializePageSuccess(jsonResponse.result));
    } catch (err) {
      pageDispatch(ACTIONS.initializePageFailure(err));
    }
  };

  useEffect(() => {
    initializePage();

    return () => {};
  }, []);

  const beginWork = (doRedirect) => {
    pageDispatch(ACTIONS.beginWorkRequest());

    return service.beginWork()
      .then(responseJson => {
        const { result } = responseJson;
        if (result.nextEncounterId === NO_WORKABLE_ENCOUNTERS) {
          alert('No workable encounters were found');
        } else if (result.nextEncounterId === ALL_ENCOUNTERS_LOCKED) {
          alert('All workable encounters are locked by other users. Please try again later.');
        } else {
          const targetUrl = `/Medicare/ClientReview/Work/Detail/${result.nextEncounterId}`;
          doRedirect(targetUrl);
        }
        pageDispatch(ACTIONS.beginWorkSuccess());
      })
      .catch(e => pageDispatch(ACTIONS.beginWorkFailure(e.toString())));
  };

  const acceptAllReviews = () => {
    pageDispatch(ACTIONS.acceptAllReviewsRequest());

    return service.acceptAllReviews()
      .then(responseJson => {
        const { result } = responseJson;

        pageDispatch(ACTIONS.acceptAllReviewsSuccess());

        return result.numberOfReviewsAccepted || 0;
      })
      .catch(e => pageDispatch(ACTIONS.acceptAllReviewsFailure(e.toString())));
  };

  const searchReviews = searchConfiguration => {
    return searchApi.searchReviews(searchConfiguration);
  };

  const boundActions = {
    beginWork,
    acceptAllReviews,
    searchReviews,
    changeSearchConfiguration: searchApi.changeConfigurationValue
  };

  const getIsRequestInProgress = () => {
    return pageState.isRequestInProgress || searchState.isRequestInProgress;
  };

  const getStatusMessage = () => {
    if (pageState.isRequestInProgress === true) {
      return pageState.statusMessage;
    }

    if (searchState.isRequestInProgress === true) {
      return searchState.statusMessage;
    }
  };

  const getIsErrorStatus = () => {
    if (pageState.status === PAGE_STATUSES.ERROR || searchState.status === PAGE_STATUSES.ERROR) {
      return true;
    }
  };

  const getErrorMessage = () => {
    if (pageState.status === PAGE_STATUSES.ERROR) {
      return pageState.errorMessage;
    }

    if (searchState.status === PAGE_STATUSES.ERROR) {
      return searchState.errorMessage;
    }

    return null;
  };

  return [
    { ...pageState,
      reviewSearch: { ...searchState },
      isRequestInProgress: getIsRequestInProgress(),
      statusMessage: getStatusMessage(),
      status: getIsErrorStatus() === true ? PAGE_STATUSES.ERROR : pageState.status,
      errorMessage: getErrorMessage()
    },
    boundActions
  ];
}

export { useClientReviewLandingPage };
